import { useEffect, useState } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { linkTo, navigate } from '../router';
import { Campfire } from '../ui/Campfire';
import { Shell } from './Shell';

// /signin: Hearth's own sign-in page, as the canvas board. Each button goes straight to Google or
// Discord (`identity_provider`), so Cognito's own page never shows; then back to `returnTo`.

export type Provider = 'Google' | 'Discord';
type Status = ReturnType<typeof useAuth>['status'];

export function SignIn({ returnTo }: { returnTo: string }) {
  const { status, signIn } = useAuth();
  const [going, setGoing] = useState<Provider>();
  // Already signed in (or just back from the provider): straight on.
  useEffect(() => {
    if (status === 'signed-in') navigate(returnTo, { replace: true });
  }, [status, returnTo]);
  return (
    <Shell>
      <SignInView
        status={status}
        going={going}
        onSignIn={(provider) => {
          setGoing(provider);
          void signIn({ returnTo, provider });
        }}
      />
    </Shell>
  );
}

/** The page, given where sign-in stands (no navigation: easy to test). */
export function SignInView({ status, going, onSignIn }: { status: Status; going?: Provider; onSignIn: (provider: Provider) => void }) {
  const ready = status === 'signed-out';
  return (
    <section className="join-card">
      <span className="join-fire"><Campfire state="lit" height={70} /></span>
      <h1>Sign in to Hearth</h1>
      {status === 'unavailable' && <p className="notice" role="alert">Hearth can't be reached right now. Please try again in a moment.</p>}
      <div className="join-signin">
        <SignInButtons going={going} disabled={!ready || !!going} onSignIn={onSignIn} />
      </div>
      <p className="signin-terms">By continuing you agree to Hearth's <a {...linkTo('/terms')}>Terms</a> and <a {...linkTo('/privacy')}>Privacy Policy</a>.</p>
    </section>
  );
}

/**
 * The two sign-ins, here and on the join page: Discord first and recommended (owner, 2026-11-01:
 * it's the sign-in the Discord bot knows), then Google.
 */
export function SignInButtons({ going, disabled = false, onSignIn }: { going?: Provider | undefined; disabled?: boolean; onSignIn: (provider: Provider) => void }) {
  const label = (provider: Provider) => (going === provider ? `Opening ${provider}…` : `Continue with ${provider}`);
  return (
    <>
      <button className="btn btn-primary btn-tall signin-rec" type="button" disabled={disabled} onClick={() => onSignIn('Discord')}>
        <DiscordMark />
        {label('Discord')}
        <span className="signin-rec-tag">Recommended</span>
      </button>
      <p className="signin-why">Wake and sleep your servers right from Discord.</p>
      <div className="signin-or">or</div>
      <button className="btn btn-tall" type="button" disabled={disabled} onClick={() => onSignIn('Google')}>
        <GoogleMark />
        {label('Google')}
      </button>
    </>
  );
}

// Each brand's mark in the app's own two tones (not their colours), as on the board.
function GoogleMark() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path fill="currentColor" d="M21.6 12.2c0-.7-.1-1.4-.2-2H12v3.8h5.4a4.6 4.6 0 0 1-2 3v2.5h3.2c1.9-1.7 3-4.3 3-7.3z" />
      <path fill="var(--ash)" d="M12 22c2.7 0 5-.9 6.6-2.4l-3.2-2.5c-.9.6-2 1-3.4 1a6 6 0 0 1-5.6-4.1H3.1v2.6A10 10 0 0 0 12 22z" />
      <path fill="currentColor" d="M6.4 14a6 6 0 0 1 0-3.9V7.5H3.1a10 10 0 0 0 0 9z" />
      <path fill="var(--ash)" d="M12 5.9c1.5 0 2.8.5 3.8 1.5l2.9-2.9A10 10 0 0 0 3.1 7.5l3.3 2.6A6 6 0 0 1 12 5.9z" />
    </svg>
  );
}

export function DiscordMark() {
  return (
    <svg width="20" height="18" viewBox="0 0 24 18" aria-hidden="true" focusable="false">
      <path
        fill="currentColor"
        d="M20.3 1.5A19.6 19.6 0 0 0 15.4 0l-.6 1.3a18.2 18.2 0 0 0-5.5 0L8.7 0a19.5 19.5 0 0 0-4.9 1.5A20.3 20.3 0 0 0 .3 15.2a19.7 19.7 0 0 0 6 3l1.3-2.1-2-1 .5-.4a14 14 0 0 0 12 0l.5.4-2 1 1.3 2.1a19.6 19.6 0 0 0 6-3A20.2 20.2 0 0 0 20.3 1.5zM8 12.4c-1.2 0-2.2-1.1-2.2-2.4S6.8 7.6 8 7.6s2.2 1.1 2.2 2.4-1 2.4-2.2 2.4zm8 0c-1.2 0-2.2-1.1-2.2-2.4s1-2.4 2.2-2.4 2.2 1.1 2.2 2.4-1 2.4-2.2 2.4z"
      />
    </svg>
  );
}
